import { initSentry } from '@/lib/sentry';
initSentry();

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from '@/App'
import '@/index.css'
import { logger } from '@/utils/logger'

// Touch target validation (development only)
if (import.meta.env.DEV) {
  import('@/utils/touch-target-validator').then(({ setupTouchTargetValidation }) => {
    setupTouchTargetValidation();
  }).catch((error) => {
    logger.error('[TouchTargetValidator] Failed to load:', error);
  });
}

// Viewport overflow detection (development only) - Issue #612
if (import.meta.env.DEV) {
  import('@/utils/viewportOverflowDetector').then(({ initViewportOverflowDetector }) => {
    initViewportOverflowDetector();
  }).catch((error) => {
    logger.error('[ViewportOverflowDetector] Failed to load:', error);
  });
}

// Initialize LogRocket only in production
if (import.meta.env.PROD && import.meta.env.VITE_LOGROCKET_PROJECT_ID) {
  import('logrocket').then((LogRocket) => {
    LogRocket.default.init(import.meta.env.VITE_LOGROCKET_PROJECT_ID);
  }).catch((error) => {
    logger.error('[LogRocket] Failed to initialize:', error);
  });
}

// Expose test helpers for E2E tests (VITE_TEST_MODE=true)
if (import.meta.env.VITE_TEST_MODE === 'true') {
  import('./lib/sync').then(({ queueSyncItem, syncAllPending, getPendingSyncItems }) => {
    (window as any).__testHelpers__ = {
      queueSyncItem,
      syncAllPending,
      getSyncQueue: getPendingSyncItems,
    };
    logger.info('✓ Test helpers exposed on window.__testHelpers__');
  }).catch((error) => {
    logger.error('[TestHelpers] Failed to load sync functions:', error);
  });
}

// Service Worker Registration
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        logger.info('[App] Service Worker registered successfully:', { scope: registration.scope });

        // Check for updates on page load
        registration.update();

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          
          logger.info('[App] New Service Worker found, installing...');

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available, notify user
              logger.info('[App] New version available! Refresh to update.');
              if (confirm('New version available! Reload to update?')) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          });
        });

        // Handle controller change (new service worker activated)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          logger.info('[App] New Service Worker activated, reloading...');
          window.location.reload();
        });
      })
      .catch((error) => {
        logger.error('[App] Service Worker registration failed:', error);
      });
  });
} else {
  logger.warn('[App] Service Workers not supported in this browser');
}

const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <App />
  )
}
